import { Error } from "../_types";
import { useRouter } from "next/router";
import { createOptin } from "@/api/optin";
import { LeadContext } from "@/context/lead";
import { getUserAgent } from "@/api/user-agent";
import { createTracking } from "@/api/tracking";
import { KeywordContext } from "@/context/keyword";
import { getIpGeolocation } from "@/api/ip-geolocation";
import { useState, useCallback, useContext } from "react";
import { createEnergyLead } from "@/utils/formatApiError";

export const useEnergyJourney = () => {
	const {
		source,
		fbclid,
		msclkid,
		gclid,
		campaignid,
		adid,
		adsetid,
		targetid,
		adgroupid,
		keyword,
	} = useContext(KeywordContext);
	const router = useRouter();
	const [address, setAddress] = useState([]);
	const { postcode } = useContext(LeadContext);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<Error | null>(null);
	const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
	const [userAnswers, setUserAnswers] = useState<Record<number, string>>({});
	const [contactDetails, setContactDetails] = useState({
		firstName: "",
		lastName: "",
		email: "",
		contactNumber: "",
	});

	const handleSaveAnswer = useCallback(
		(answer: string) => {
			setUserAnswers((prevAnswers) => ({
				...prevAnswers,
				[currentQuestionIndex]: answer,
			}));

			setCurrentQuestionIndex(currentQuestionIndex + 1);
		},
		[currentQuestionIndex]
	);

	const handleSetAddress = useCallback((newAddress: any) => {
		setAddress(newAddress);
	}, []);

	const handleSubmit = async (contact: {
		firstName: string;
		lastName: string;
		email: string;
		contactNumber: string;
		broadbandOptIn: boolean;
	}) => {
		setLoading(true);

		const { error, data } = await createEnergyLead({
			postcode,
			quoteType: userAnswers[0],
			currentProvider: userAnswers[1],
			typeOfEnergy: userAnswers[2],
			paymentMethod: userAnswers[3],
			reason: userAnswers[4],
			homeowner: userAnswers[5],
			firstName: contact.firstName,
			lastName: contact.lastName,
			email: contact.email,
			contactNumber: contact.contactNumber,
			source,
			fbclid,
			msclkid,
			gclid,
			campaignid,
			adid,
			adsetid,
			targetid,
			adgroupid,
			keyword,
		});

		if (error) {
			setError(error);
			setLoading(false);
			return;
		}

		if (data && data?.uuid !== undefined) {
			await createOptin({
				optin_service_uuid: process.env.NEXT_PUBLIC_ENERGY_OPTIN_SERVICE_UUID,
				lead_uuid: data.uuid,
				phone: true,
				email: true,
				sms: true,
				mail: true,
			});

			if (contact.broadbandOptIn) {
				await createOptin({
					optin_service_uuid:
						process.env.NEXT_PUBLIC_BROADBAND_OPTIN_SERVICE_UUID,
					lead_uuid: data.uuid,
					phone: true,
					email: true,
					sms: true,
					mail: true,
				});
			}

			const { data: ipGeolocation } = await getIpGeolocation();

			if (window) {
				const { data: userAgentData } = await getUserAgent(
					window.navigator.userAgent
				);

				await createTracking({
					lead_uuid: data.uuid,
					ip_address: ipGeolocation?.ip_address,
					country: ipGeolocation?.country,
					city: ipGeolocation?.city,
					region: ipGeolocation?.region,
					user_agent: window.navigator.userAgent,
					browser: { ...userAgentData?.browser },
					operatingSystem: { ...userAgentData?.os },
					device: { ...userAgentData?.device },
				});
			} else {
				await createTracking({
					lead_uuid: data.uuid,
					ip: ipGeolocation?.ip_address,
					country: ipGeolocation?.country,
					city: ipGeolocation?.city,
					region: ipGeolocation?.region,
				});
			}
		}

		setContactDetails(contact);

		return router.push("/energy/thank-you").then(() => {
			setLoading(false);
		});
	};

	const resetEnergyJourney = useCallback(() => {
		setCurrentQuestionIndex(0);
		setUserAnswers({});
		setAddress([]);
		setContactDetails({
			firstName: "",
			lastName: "",
			email: "",
			contactNumber: "",
		});
	}, []);

	return {
		currentQuestionIndex,
		userAnswers,
		address,
		contactDetails,
		handleSaveAnswer,
		handleSetAddress,
		handleSubmit,
		resetEnergyJourney,
		loading,
		error,
	};
};
